'use client'

/**
 * Sistema global de confirmação imperativo — substituto profissional de window.confirm().
 *
 * Uso:
 *   const ok = await confirmDialog({ title: 'Eliminar?', description: 'Esta acção é irreversível.' })
 *   if (ok) doSomething()
 *
 * Ou com shorthand:
 *   const ok = await confirmDialog('Eliminar este item?')
 */

import { useEffect, useRef, useState } from 'react'
import { create } from 'zustand'
import { AlertTriangle, Trash2, HelpCircle, X, Pencil } from 'lucide-react'

// ── Tipos ─────────────────────────────────────────────────────────────────────

export type ConfirmVariant = 'danger' | 'warning' | 'default'

interface ConfirmOptions {
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: ConfirmVariant
}

interface ConfirmDialogState {
  isOpen: boolean
  options: ConfirmOptions
  resolve: ((value: boolean) => void) | null
  _open: (options: ConfirmOptions, resolve: (v: boolean) => void) => void
  _close: (value: boolean) => void
}

// ── Store Zustand ─────────────────────────────────────────────────────────────

const useConfirmStore = create<ConfirmDialogState>((set, get) => ({
  isOpen: false,
  options: { title: '' },
  resolve: null,
  _open: (options, resolve) => set({ isOpen: true, options, resolve }),
  _close: (value) => {
    get().resolve?.(value)
    set({ isOpen: false, resolve: null })
  },
}))

// ── API imperativa ────────────────────────────────────────────────────────────

export function confirmDialog(options: ConfirmOptions | string): Promise<boolean> {
  return new Promise((resolve) => {
    const normalised: ConfirmOptions =
      typeof options === 'string' ? { title: options, variant: 'danger' } : options
    useConfirmStore.getState()._open(normalised, resolve)
  })
}

// ── Estilos por variante ──────────────────────────────────────────────────────

const VARIANT_CONFIG: Record<ConfirmVariant, {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>
  iconBg: string
  iconColor: string
  btnBg: string
  btnHover: string
  btnText: string
}> = {
  danger: {
    icon: Trash2,
    iconBg: 'rgba(239, 68, 68, 0.10)',
    iconColor: '#ef4444',
    btnBg: '#ef4444',
    btnHover: '#dc2626',
    btnText: '#ffffff',
  },
  warning: {
    icon: AlertTriangle,
    iconBg: 'rgba(245, 158, 11, 0.10)',
    iconColor: '#f59e0b',
    btnBg: '#f59e0b',
    btnHover: '#d97706',
    btnText: '#ffffff',
  },
  default: {
    icon: HelpCircle,
    iconBg: 'rgba(240, 104, 35, 0.10)',
    iconColor: '#F06823',
    btnBg: '#F06823',
    btnHover: '#D45515',
    btnText: '#ffffff',
  },
}

// ── Componente ────────────────────────────────────────────────────────────────

export function ConfirmDialogRoot() {
  const { isOpen, options, _close } = useConfirmStore()
  const cancelRef = useRef<HTMLButtonElement>(null)

  const variant = options.variant ?? 'danger'
  const cfg = VARIANT_CONFIG[variant]
  const Icon = cfg.icon

  // Foco no botão cancelar ao abrir (acessibilidade)
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => cancelRef.current?.focus(), 50)
    }
  }, [isOpen])

  // ESC fecha sem confirmar
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) _close(false)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, _close])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0, 0, 0, 0.55)', backdropFilter: 'blur(4px)' }}
      onClick={() => _close(false)}
    >
      <div
        className="relative w-full max-w-sm rounded"
        style={{
          background: 'var(--c-surface)',
          border: '1px solid var(--c-border)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25), 0 4px 16px rgba(0,0,0,0.12)',
          animation: 'confirmSlideIn 0.18s cubic-bezier(0.34, 1.2, 0.64, 1)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Botão fechar */}
        <button
          onClick={() => _close(false)}
          className="absolute top-3.5 right-3.5 p-1 rounded-sm transition-colors"
          style={{ color: 'var(--c-text-muted)' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-surface-3)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          aria-label="Fechar"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-6 pt-6 pb-5">
          {/* Ícone */}
          <div
            className="w-10 h-10 rounded flex items-center justify-center mb-4"
            style={{ background: cfg.iconBg }}
          >
            <Icon className="w-5 h-5" style={{ color: cfg.iconColor }} />
          </div>

          {/* Título */}
          <h2
            className="font-semibold leading-snug mb-1.5"
            style={{ fontSize: '1rem', letterSpacing: '-0.02em', color: 'var(--c-text)' }}
          >
            {options.title}
          </h2>

          {/* Descrição */}
          {options.description && (
            <p
              className="text-sm leading-relaxed"
              style={{ color: 'var(--c-text-3)' }}
            >
              {options.description}
            </p>
          )}
        </div>

        {/* Linha separadora */}
        <div style={{ height: '1px', background: 'var(--c-border)' }} />

        {/* Botões */}
        <div className="px-6 py-4 flex justify-end gap-2.5">
          <button
            ref={cancelRef}
            onClick={() => _close(false)}
            className="px-4 py-2 text-sm font-medium rounded-sm transition-colors"
            style={{
              background: 'var(--c-surface-3)',
              color: 'var(--c-text-2)',
              border: '1px solid var(--c-border)',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-border)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'var(--c-surface-3)')}
          >
            {options.cancelLabel ?? 'Cancelar'}
          </button>
          <button
            onClick={() => _close(true)}
            className="px-4 py-2 text-sm font-semibold rounded-sm transition-colors"
            style={{ background: cfg.btnBg, color: cfg.btnText }}
            onMouseEnter={e => (e.currentTarget.style.background = cfg.btnHover)}
            onMouseLeave={e => (e.currentTarget.style.background = cfg.btnBg)}
          >
            {options.confirmLabel ?? 'Confirmar'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes confirmSlideIn {
          from { opacity: 0; transform: scale(0.94) translateY(6px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);   }
        }
      `}</style>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// Prompt imperativo — substituto profissional de window.prompt()
//
//   const url = await promptDialog({ title: 'URL do link', placeholder: 'https://...' })
//   if (url) doSomething(url)
// ════════════════════════════════════════════════════════════════════════════

interface PromptOptions {
  title: string
  description?: string
  placeholder?: string
  defaultValue?: string
  confirmLabel?: string
  cancelLabel?: string
}

interface PromptDialogState {
  isOpen: boolean
  options: PromptOptions
  resolve: ((value: string | null) => void) | null
  _open: (options: PromptOptions, resolve: (v: string | null) => void) => void
  _close: (value: string | null) => void
}

const usePromptStore = create<PromptDialogState>((set, get) => ({
  isOpen: false,
  options: { title: '' },
  resolve: null,
  _open: (options, resolve) => set({ isOpen: true, options, resolve }),
  _close: (value) => {
    get().resolve?.(value)
    set({ isOpen: false, resolve: null })
  },
}))

/** Pede um texto ao utilizador. Resolve com a string, ou null se cancelado. */
export function promptDialog(options: PromptOptions | string): Promise<string | null> {
  return new Promise((resolve) => {
    const normalised: PromptOptions =
      typeof options === 'string' ? { title: options } : options
    usePromptStore.getState()._open(normalised, resolve)
  })
}

export function PromptDialogRoot() {
  const { isOpen, options, _close } = usePromptStore()
  const [value, setValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      setValue(options.defaultValue ?? '')
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [isOpen, options.defaultValue])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) _close(null)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, _close])

  if (!isOpen) return null

  const submit = () => _close(value.trim() || null)

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0, 0, 0, 0.55)', backdropFilter: 'blur(4px)' }}
      onClick={() => _close(null)}
    >
      <div
        className="relative w-full max-w-sm rounded"
        style={{
          background: 'var(--c-surface)',
          border: '1px solid var(--c-border)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25), 0 4px 16px rgba(0,0,0,0.12)',
          animation: 'confirmSlideIn 0.18s cubic-bezier(0.34, 1.2, 0.64, 1)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={() => _close(null)}
          className="absolute top-3.5 right-3.5 p-1 rounded-sm transition-colors"
          style={{ color: 'var(--c-text-muted)' }}
          aria-label="Fechar"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-6 pt-6 pb-5">
          <div
            className="w-10 h-10 rounded flex items-center justify-center mb-4"
            style={{ background: 'rgba(240, 104, 35, 0.10)' }}
          >
            <Pencil className="w-5 h-5" style={{ color: '#F06823' }} />
          </div>
          <h2
            className="font-semibold leading-snug mb-1.5"
            style={{ fontSize: '1rem', letterSpacing: '-0.02em', color: 'var(--c-text)' }}
          >
            {options.title}
          </h2>
          {options.description && (
            <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--c-text-3)' }}>
              {options.description}
            </p>
          )}
          <input
            ref={inputRef}
            className="input mt-1"
            placeholder={options.placeholder}
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submit() }}
          />
        </div>

        <div style={{ height: '1px', background: 'var(--c-border)' }} />

        <div className="px-6 py-4 flex justify-end gap-2.5">
          <button
            onClick={() => _close(null)}
            className="px-4 py-2 text-sm font-medium rounded-sm transition-colors"
            style={{
              background: 'var(--c-surface-3)',
              color: 'var(--c-text-2)',
              border: '1px solid var(--c-border)',
            }}
          >
            {options.cancelLabel ?? 'Cancelar'}
          </button>
          <button
            onClick={submit}
            className="px-4 py-2 text-sm font-semibold rounded-sm transition-colors"
            style={{ background: '#F06823', color: '#ffffff' }}
          >
            {options.confirmLabel ?? 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  )
}
